import React, { useEffect, useMemo, useState } from "react";
import employeeService from "../api/employeeService";

const initialForm = {
    id: null,
    name: "",
    lastName: "",
    email: "",
    phone: "",
    userType: "Individual",
    companyId: "",
    contractEndDate: "",
    status: "Pending",
};

export default function EmployeeAdminPage() {
    const [employees, setEmployees] = useState([]);
    const [form, setForm] = useState(initialForm);
    const [editingId, setEditingId] = useState(null);
    const [statusFilter, setStatusFilter] = useState("All");
    const [search, setSearch] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    const loadEmployees = async () => {
        try {
            setLoading(true);
            setError("");
            const data = await employeeService.getAll();
            setEmployees(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(e.message || "Failed to load employees");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadEmployees();
    }, []);

    const filteredEmployees = useMemo(() => {
        return employees.filter((e) => {
            const text = `${e.name ?? ""} ${e.lastName ?? ""} ${e.email ?? ""}`.toLowerCase();
            const matchesSearch = text.includes(search.toLowerCase());
            const matchesStatus = statusFilter === "All" || e.status === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [employees, search, statusFilter]);

    const handleChange = (e) => {
        setForm((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const resetForm = () => {
        setForm(initialForm);
        setEditingId(null);
    };

    const toPayload = () => ({
        ...(editingId ? { id: editingId } : {}),
        name: form.name,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        userType: form.userType,
        companyId: form.companyId ? Number(form.companyId) : null,
        contractEndDate: form.contractEndDate || null,
        status: form.status,
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setError("");

            if (editingId) {
                await employeeService.update(editingId, toPayload());
            } else {
                await employeeService.create(toPayload());
            }

            resetForm();
            await loadEmployees();
        } catch (e) {
            setError(e.message || "Failed to save employee");
        }
    };

    const handleEdit = (employee) => {
        setEditingId(employee.id);
        setForm({
            id: employee.id,
            name: employee.name ?? "",
            lastName: employee.lastName ?? "",
            email: employee.email ?? "",
            phone: employee.phone ?? "",
            userType: employee.userType ?? "Individual",
            companyId: employee.companyId ?? "",
            contractEndDate: employee.contractEndDate
                ? employee.contractEndDate.substring(0, 10)
                : "",
            status: employee.status ?? "Pending",
        });
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this employee?")) return;

        try {
            await employeeService.remove(id);
            await loadEmployees();
        } catch (e) {
            setError(e.message || "Failed to delete employee");
        }
    };

    const runAutoEndContracts = async () => {
        await employeeService.autoEndContracts();
        await loadEmployees();
    };

    const runAutoTerminatePending = async () => {
        await employeeService.autoTerminatePending();
        await loadEmployees();
    };

    if (loading) return <p className="p-6">Loading employees...</p>;

    return (
        <div className="max-w-6xl mx-auto p-6 space-y-6">
            <h1 className="text-2xl font-bold">Employee Management</h1>

            {error && <p className="text-red-600">{error}</p>}

            <div className="flex gap-3">
                <button
                    onClick={runAutoEndContracts}
                    className="bg-orange-600 text-white px-4 py-2 rounded"
                >
                    Auto end contracts
                </button>

                <button
                    onClick={runAutoTerminatePending}
                    className="bg-red-600 text-white px-4 py-2 rounded"
                >
                    Auto terminate pending
                </button>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-4 rounded shadow grid grid-cols-2 gap-3">
                <input name="name" value={form.name} onChange={handleChange} placeholder="Name" className="border p-2 rounded" required />
                <input name="lastName" value={form.lastName} onChange={handleChange} placeholder="Last name" className="border p-2 rounded" required />
                <input name="email" value={form.email} onChange={handleChange} placeholder="Email" className="border p-2 rounded" required />
                <input name="phone" value={form.phone} onChange={handleChange} placeholder="Phone" className="border p-2 rounded" />

                <select name="userType" value={form.userType} onChange={handleChange} className="border p-2 rounded">
                    <option value="Individual">Individual</option>
                    <option value="LegalEntity">LegalEntity</option>
                </select>

                <input name="companyId" value={form.companyId} onChange={handleChange} placeholder="Company ID" type="number" className="border p-2 rounded" />

                <input name="contractEndDate" value={form.contractEndDate} onChange={handleChange} type="date" className="border p-2 rounded" />

                <select name="status" value={form.status} onChange={handleChange} className="border p-2 rounded">
                    <option value="Pending">Pending</option>
                    <option value="Active">Active</option>
                    <option value="ContractEnded">ContractEnded</option>
                    <option value="Terminated">Terminated</option>
                </select>

                <div className="col-span-2 flex gap-2">
                    <button className="bg-blue-600 text-white px-4 py-2 rounded">
                        {editingId ? "Update employee" : "Create employee"}
                    </button>

                    {editingId && (
                        <button type="button" onClick={resetForm} className="bg-gray-300 px-4 py-2 rounded">
                            Cancel
                        </button>
                    )}
                </div>
            </form>

            <div className="flex gap-3">
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name or email"
                    className="border p-2 rounded flex-1"
                />

                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="border p-2 rounded"
                >
                    <option value="All">All</option>
                    <option value="Pending">Pending</option>
                    <option value="Active">Active</option>
                    <option value="ContractEnded">ContractEnded</option>
                    <option value="Terminated">Terminated</option>
                </select>
            </div>

            <table className="w-full border text-sm bg-white">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border p-2">ID</th>
                        <th className="border p-2">Name</th>
                        <th className="border p-2">Email</th>
                        <th className="border p-2">Phone</th>
                        <th className="border p-2">Company</th>
                        <th className="border p-2">Status</th>
                        <th className="border p-2">Contract end</th>
                        <th className="border p-2">Actions</th>
                    </tr>
                </thead>

                <tbody>
                    {filteredEmployees.map((e) => (
                        <tr key={e.id}>
                            <td className="border p-2">{e.id}</td>
                            <td className="border p-2">{e.name} {e.lastName}</td>
                            <td className="border p-2">{e.email}</td>
                            <td className="border p-2">{e.phone || "�"}</td>
                            <td className="border p-2">{e.companyId || "�"}</td>
                            <td className="border p-2">{e.status}</td>
                            <td className="border p-2">
                                {e.contractEndDate ? new Date(e.contractEndDate).toLocaleDateString() : "�"}
                            </td>
                            <td className="border p-2 space-x-2">
                                <button onClick={() => handleEdit(e)} className="text-blue-600">
                                    Edit
                                </button>
                                <button onClick={() => handleDelete(e.id)} className="text-red-600">
                                    Delete
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {filteredEmployees.length === 0 && (
                <p className="text-gray-500">No employees found.</p>
            )}
        </div>
    );
}